import React, { useEffect, useRef, useState } from 'react'

interface ExpandableContainerProps {
  id: string
  expandableCondition: boolean
  children?: React.ReactNode
  tot_margin_y?: number
  tot_padding_y?: number
  className?: string
}

/** Permette di mostrare e nascondere il suo contenuto tramite un'animazione fluida si scorrimento
 *
 * @param id id del componente in questione
 * @param expandableCondition la condizione booleane per cui, se è true, il contenuto del componente verrà mostrato
 * @param children il contenuto da mostrare
 * @param className le classi da associare al componente
 * @param tot_margin_y il margine verticale complessivo utilizzato nel children
 * @param tot_padding_y il padding verticale complessivo utilizzato nel children
 *
 * @desc N.B. i margini e i padding da passare sono fondamentali per la corretta animazione e quindi visualizzazione del contenuto
 * */
const ExpandableContainer = ({
  id,
  expandableCondition,
  children,
  className,
  tot_padding_y = 0,
  tot_margin_y = 0
}: ExpandableContainerProps) => {
  const expandableRef = useRef<HTMLDivElement>(null)
  const [expandableHeight, setExpandableHeight] = useState(0)

  // adatta dinamicamente l'altezza della div al contenuto della pagina (anche quando si esegue il resize della finestra)
  useEffect(() => {
    if (!expandableHeight || !expandableCondition || !expandableRef.current) return undefined
    const resizeObserver = new ResizeObserver((el) => {
      setExpandableHeight(el[0].contentRect.height + tot_padding_y + tot_margin_y)
    })
    resizeObserver.observe(expandableRef.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [expandableHeight, expandableCondition, tot_margin_y, tot_padding_y])

  // setta la nuova altezza quando il contenuto diventa visibile
  useEffect(() => {
    if (expandableCondition) {
      setExpandableHeight(expandableRef.current?.clientHeight ?? 0)
    } else setExpandableHeight(0)
  }, [expandableCondition])

  return (
    <div
      id={id + '-expandable-container'}
      className={className}
      style={{
        height: `${expandableHeight}px`
      }}>
      <div ref={expandableRef}>{children}</div>
    </div>
  )
}

export default ExpandableContainer
